import { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import SearchIcon from '@mui/icons-material/Search';
import SlideshowOutlinedIcon from '@mui/icons-material/SlideshowOutlined';
import { api, type DeckSummary } from '../lib/api';
import { useDeckStore } from '../store/deckStore';
import { navigate, paths } from '../lib/router';
import { ConfirmDialog } from './ConfirmDialog';

interface Props {
  open: boolean;
  onClose: () => void;
}

function formatUpdated(iso: string): string {
  const d = new Date(iso);
  const sameDay = d.toDateString() === new Date().toDateString();
  return sameDay
    ? `Today, ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
    : d.toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' });
}

export function DeckLibrary({ open, onClose }: Props) {
  const currentId = useDeckStore((s) => s.deckId);
  const newDeck = useDeckStore((s) => s.newDeck);
  const duplicateDeck = useDeckStore((s) => s.duplicateDeck);
  const deleteDeck = useDeckStore((s) => s.deleteDeck);
  const [decks, setDecks] = useState<DeckSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState('');
  const [menu, setMenu] = useState<{ anchor: HTMLElement; deck: DeckSummary } | null>(null);
  const [toDelete, setToDelete] = useState<DeckSummary | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const r = await api.listDecks();
      setDecks(r.decks);
    } catch (err) {
      setDecks([]);
      setError(err instanceof Error ? `Couldn't load decks: ${err.message}` : "Couldn't load decks.");
    }
  }, []);

  const onOpen = () => {
    setDecks(null);
    setQuery('');
    void load();
  };

  const filtered = useMemo(
    () => (decks ?? []).filter((d) => d.title.toLowerCase().includes(query.trim().toLowerCase())),
    [decks, query],
  );

  const run = async (fn: () => Promise<unknown>, closeAfter = true) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      if (closeAfter) onClose();
      else await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="sm"
        aria-labelledby="deck-library-title"
        slotProps={{ transition: { onEnter: onOpen } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 7 }}>
          <Typography id="deck-library-title" variant="h6" component="h2" sx={{ flex: 1 }}>
            Your decks
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} disabled={busy} onClick={() =>
              void run(async () => {
                const id = await newDeck();
                if (id) navigate(paths.edit(id));
              })
            }>
            New deck
          </Button>
          <IconButton aria-label="Close" onClick={onClose} sx={{ position: 'absolute', right: 16, top: 18 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            placeholder="Search decks"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            slotProps={{
              htmlInput: { 'aria-label': 'Search decks' },
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18 }} />
                  </InputAdornment>
                ),
              },
            }}
            sx={{ mb: 1.5 }}
          />
          {error && (
            <Alert severity="error" sx={{ mb: 1.5 }}>
              {error}
            </Alert>
          )}
          {decks === null ? (
            <Box sx={{ display: 'grid', placeItems: 'center', py: 6 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Stack sx={{ minHeight: 240, maxHeight: 420, overflowY: 'auto', mx: -1 }}>
              {filtered.map((d) => {
                const current = d.id === currentId;
                return (
                  <Stack
                    key={d.id}
                    direction="row"
                    sx={{
                      alignItems: 'center',
                      gap: 1.5,
                      px: 1,
                      py: 0.5,
                      borderRadius: 2,
                      bgcolor: current ? 'chrome.tint' : 'transparent',
                      '&:hover': { bgcolor: current ? 'chrome.tint' : 'chrome.subtle' },
                    }}
                  >
                    <Box
                      component="button"
                      type="button"
                      disabled={busy}
                      onClick={() => void run(async () => navigate(paths.edit(d.id)))}
                      sx={{
                        all: 'unset',
                        flex: 1,
                        minWidth: 0,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        py: 1,
                        cursor: 'pointer',
                        '&:focus-visible': { outline: 2, outlineColor: 'primary.main', borderRadius: 1 },
                      }}
                    >
                      <SlideshowOutlinedIcon sx={{ color: current ? 'primary.main' : 'text.secondary' }} />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                          {d.title}
                        </Typography>
                        <Typography variant="caption" component="div">
                          {[
                            d.slide_count != null ? `${d.slide_count} slides` : null,
                            `Edited ${formatUpdated(d.updated_at)}`,
                            current ? 'Open now' : null,
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </Typography>
                      </Box>
                    </Box>
                    <IconButton
                      aria-label={`More actions for ${d.title}`}
                      onClick={(e) => setMenu({ anchor: e.currentTarget, deck: d })}
                    >
                      <MoreHorizIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                );
              })}
              {!filtered.length && !error && (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 6 }}>
                  {query ? `No decks match “${query}”.` : 'No saved decks yet.'}
                </Typography>
              )}
            </Stack>
          )}
        </DialogContent>
      </Dialog>

      <Menu
        anchorEl={menu?.anchor}
        open={Boolean(menu)}
        onClose={() => setMenu(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { width: 180 } } }}
      >
        <MenuItem
          onClick={() => {
            const d = menu?.deck;
            setMenu(null);
            if (d) void run(async () => navigate(paths.edit(await duplicateDeck(d.id))));
          }}
        >
          <ListItemIcon>
            <ContentCopyOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Duplicate" />
        </MenuItem>
        <MenuItem
          sx={{ color: 'error.main' }}
          onClick={() => {
            setToDelete(menu?.deck ?? null);
            setMenu(null);
          }}
        >
          <ListItemIcon sx={{ color: 'inherit' }}>
            <DeleteOutlineIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Delete" />
        </MenuItem>
      </Menu>

      <ConfirmDialog
        open={Boolean(toDelete)}
        title="Delete deck?"
        message={`“${toDelete?.title ?? ''}” will be permanently deleted. This can't be undone.`}
        confirmLabel="Delete deck"
        destructive
        onClose={() => setToDelete(null)}
        onConfirm={() => {
          const d = toDelete;
          if (d) void run(() => deleteDeck(d.id), false);
        }}
      />
    </>
  );
}
